import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { getSignupCountryFromCountryCode } from "@simplyk/common";

import { SignUpSessionStorage } from "../store/SignUpSessionStorage";
import { CloudflareCountryCache } from "../store/types";

import { isProduction } from "@/common/constants/env";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export const useUserCountryByCloudflareHeader = ({ countryOverride }: { countryOverride?: string } = {}) => {
  const fetchUserCountryByCloudflareHeader = useCallback(async () => {
    const response = await fetch(process.env.NEXT_PUBLIC_API_URL + "/application/user-country", {
      method: "GET",
      referrerPolicy: "unsafe-url",
    });
    const data: { countryCode: string } = await response.json();

    return data.countryCode;
  }, []);

  const [countryByCloudflareHeader, setCountryByCloudflareHeader] = useState<CloudflareCountryCache | undefined>(
    undefined
  );
  const [isCountryByCloudflareHeaderLoading, setIsCountryByCloudflareHeaderLoading] = useState<boolean>(true);

  useIsomorphicLayoutEffect(() => {
    if (!isProduction && countryOverride) {
      const mapped = getSignupCountryFromCountryCode(countryOverride);
      setCountryByCloudflareHeader(mapped);
      setIsCountryByCloudflareHeaderLoading(false);
      if (mapped) {
        SignUpSessionStorage.setCloudflareCountry(mapped);
      }
      return;
    }

    // Reuse the cached Cloudflare country resolved on an earlier register step
    // so subsequent steps don't wait on another /application/user-country round-trip.
    const cached = SignUpSessionStorage.getCloudflareCountry();
    if (cached) {
      setCountryByCloudflareHeader(cached);
      setIsCountryByCloudflareHeaderLoading(false);
      return;
    }

    const getCountryCode = async () => {
      try {
        const countryCode = await fetchUserCountryByCloudflareHeader();
        const cachedWhileFetching = SignUpSessionStorage.getCloudflareCountry();
        const mapped = cachedWhileFetching ?? getSignupCountryFromCountryCode(countryCode);
        setCountryByCloudflareHeader(mapped);
        if (mapped) {
          SignUpSessionStorage.setCloudflareCountry(mapped);
        }
      } catch {
        setCountryByCloudflareHeader(undefined);
      } finally {
        setIsCountryByCloudflareHeaderLoading(false);
      }
    };

    getCountryCode();
  }, [fetchUserCountryByCloudflareHeader, countryOverride]);

  return { countryByCloudflareHeader, isCountryByCloudflareHeaderLoading };
};
